// QualityGateRulePage.tsx — one rule's flagged heads, at
// `/quality-gate/rules/:rule`.
//
// The middle step of the drill-down: Quality gate → rule → a scored head. Each
// row is a head the rule raised a finding on, newest first, and leads to that
// head's score detail.
//
// Each head names its repository, so the family scope narrows the rows here:
// `GET /quality-gate/rules/:rule` takes no family, and the repo→family map the
// shell already has answers the question. Heads in other families are counted
// under the table rather than dropped silently.

import { ShieldCheck } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { pillClass, scoreTone } from '../../components/tone/tone';
import { LoadingState } from '../../components/state';
import { OtherFamilies } from '../../components/family/OtherFamilies';
import { ScopedEmptyState } from '../../components/family/ScopedEmptyState';
import { useRepoFamilyScope } from '../../components/family/useRepoFamilyScope';
import { useQualityGateRule } from '../../hooks/useQualityGate';
import { PageHeader, queryFreshness } from '../../components/pageHeader';
import { QualityGateQueryState } from './QualityGateQueryState';
import { qualityGateHeadPath, shortSha } from './qualityGateModel';
import { usePageTitle } from '../../hooks/usePageTitle';
import '../page.css';
import './QualityGate.css';
import { When } from '../../format/When';

export function QualityGateRulePage(): JSX.Element {
  const { rule = '' } = useParams();
  usePageTitle(rule ? `${rule} · Quality gate` : 'Quality gate');
  const detail = useQualityGateRule(rule);
  const { data, isPending, isError, error } = detail;
  const scope = useRepoFamilyScope();
  const heads = scope.rowsByRepo(data?.heads ?? [], (head) => head.repo);

  return (
    <div className="page page--wide" data-testid="quality-gate-rule-page">
      <PageHeader
        title={data?.title ?? rule}
        testId="quality-gate-rule-header"
        parent="nav.quality-gate"
        crumbs={[{ label: data?.title ?? rule }]}
        freshness={queryFreshness(detail)}
        subtitle={
          <>
          <code>{rule}</code>
          {data ? ` — ${data.description}` : null}
          </>
        }
      />

      {isPending ? (
        <LoadingState title="Loading the flagged heads…" variant="message" />
      ) : isError ? (
        <QualityGateQueryState what={`the heads ${rule} flagged`} error={error} />
      ) : heads.rows.length === 0 ? (
        <>
          <ScopedEmptyState
            icon={ShieldCheck}
            title="This rule flagged nothing in the window."
            description={`No head scored in the last ${data.window_days} days carries a finding from this rule.`}
          />
          <OtherFamilies count={heads.elsewhere} noun="flagged heads" />
        </>
      ) : (
        <section className="page__section" aria-labelledby="quality-gate-heads">
          <h2 className="page__section-title" id="quality-gate-heads">
            Flagged heads
          </h2>
          <div className="table-scroll">
            <table className="quality-gate__table" data-testid="quality-gate-heads-table">
              <thead>
                <tr>
                  <th scope="col">Head</th>
                  <th scope="col">Branch</th>
                  <th scope="col">Scored</th>
                  <th scope="col" className="quality-gate__num">
                    Score
                  </th>
                  <th scope="col" className="quality-gate__num">
                    Findings
                  </th>
                  <th scope="col" className="quality-gate__num">
                    Disputed
                  </th>
                </tr>
              </thead>
              <tbody>
                {heads.rows.map((head) => (
                  <tr
                    key={`${head.repo}@${head.sha}`}
                    data-testid={`quality-gate-head-${head.repo}@${shortSha(head.sha)}`}
                  >
                    <td>
                      <Link to={qualityGateHeadPath(head.repo, head.sha)}>
                        <code>{head.repo}@{shortSha(head.sha)}</code>
                      </Link>
                    </td>
                    <td>{head.branch}</td>
                    <td>
                      <When at={head.scored_at} />
                    </td>
                    <td className="quality-gate__num">
                      <span className={pillClass(scoreTone(head.score, head.threshold))}>
                        {head.score} / {head.threshold}
                      </span>
                    </td>
                    <td className="quality-gate__num">{head.findings}</td>
                    <td className="quality-gate__num">{head.disputes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <OtherFamilies count={heads.elsewhere} noun="flagged heads" />
        </section>
      )}
    </div>
  );
}
